import React from 'react'
import { Provider } from 'mobx-react'
import isFinite from 'lodash/isFinite'

import App from '@client/components/App'
import StartupError from '@client/components/StartupError'
import { loadItemByType } from './loadItem'
import { parseReturnUrl } from './returnUrl'
import {
  APP_CONFIG_GET_A_NEWS_LETTER_401_REDIRECT,
  APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
} from '@config/app.config'
import storage, { cancelStorage } from './Storage'
import { ganApi } from '@client/api'
import {
  ITEM_TYPE_RESPONDER,
  ITEM_TYPE_MAIL,
  ITEM_TYPE_CONFIRMATION_MAIL,
  ITEM_TYPE_FORM,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_FORM_SUBTYPE_SUCCESS,
  ITEM_TYPE_LANDING_PAGE,
  ITEM_TYPE_LANDING_PAGE_SUCCESS,
  ITEM_TYPE_SURVEY,
  ITEM_TYPE_SURVEY_THANK_YOU,
  FORMS_STEP_INDEX_FORM,
  FORMS_STEP_INDEX_FORM_SUCCESS,
  FORMS_STEP_INDEX_MAIL,
  FORMS_STEP_INDEX_PAGE,
  PAGES_STEP_INDEX_PAGE,
  PAGES_STEP_INDEX_PAGE_SUCCESS,
  PAGES_STEP_INDEX_MAIL,
  PAGES_STEP_INDEX_CONFIRMATION_PAGE,
  SURVEYS_STEP_INDEX_SURVEY,
  SURVEYS_STEP_INDEX_THANK_YOU,
  WIZARD_TYPE_FORM,
  WIZARD_TYPE_PAGE,
  WIZARD_TYPE_SURVEY,
} from '@client/components/Editor/settings'

// Captures a `next` return-URL (see utils/returnUrl) into the app model, scoped
// to this wizard's hash + type. Called by every wizard-step route action, not
// just the first — frontend-v3's publish/review screens can hand off directly
// into any content-piece step, each carrying its own `next`. Safe to call on
// every entry, including internal footer-nav/breadcrumb navigation (which links
// with no query string): AppModel.setReturnUrl keeps the existing value when the
// hash + type still match the one already captured, and only clears it for a
// genuinely different wizard. See FE-1216/FE-1218.
const captureReturnUrl = (context, wizardType) => {
  context.state.appModel.setReturnUrl(
    parseReturnUrl(context.search),
    context.params.hash,
    wizardType
  )
}

// Loads the user the editor starts with. Resolves to `{ user }`, or to
// `{ route }` when the editor can't start. A 401 goes to login, and any other
// HTTP error or a user without an id to the not-found page. A network failure
// or a 5xx says nothing about the user, so it shows an error with a retry
// instead of the not-found page (FE-1475). The user's language isn't known
// yet, so that error uses the browser's.
const loadUser = async (state) => {
  try {
    const user = await ganApi.loadUser()
    if (user && user.id) {
      return { user }
    }
  } catch (error) {
    if (error.status === 401) {
      return { route: { redirect: APP_CONFIG_GET_A_NEWS_LETTER_401_REDIRECT } }
    }
    if (!error.status || error.status >= 500) {
      state.appModel.setLocale(
        (navigator.language || '').toLowerCase().split(/[_-]/)[0]
      )
      return { route: { content: <StartupError app={state.appModel} /> } }
    }
  }
  return { route: { redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT } }
}

const routes = [
  {
    path: '/',
    async action({ next, state }) {
      const { user, route } = await loadUser(state)
      if (route) {
        return route
      }
      state.appModel.setUser(user)

      storage.setUserId(user.id)
      cancelStorage.setUserId(user.id)

      const children = await next()

      if (!children) {
        return null
      }

      if (children.redirect) {
        return children
      }

      return {
        content: (
          <Provider
            app={state.appModel}
            editor={state.editorModel}
            fontloader={state.fontloader}
            serverRender={false}
          >
            {children}
          </Provider>
        ),
      }
    },
    children: [
      {
        path: '/mails',
        async action({ next }) {
          const children = await next()

          return children
        },
        children: [
          {
            path: '/:id',
            async action(context) {
              const { state, params } = context

              storage.setItemType(ITEM_TYPE_MAIL)
              cancelStorage.setItemType(ITEM_TYPE_MAIL)

              state.savedItem = await loadItemByType(
                params.id,
                null,
                ITEM_TYPE_MAIL
              )
              state.savedItem.kind = ITEM_TYPE_MAIL

              state.editorModel.load(state.savedItem)
              state.fontloader.load(state.editorModel.getFonts())
              state.history.clear()

              if (
                !state.editorModel.id ||
                (state.editorModel.id && isFinite(Number(params.id))
                  ? parseInt(state.editorModel.id, 10) !==
                    parseInt(params.id, 10)
                  : state.editorModel.id !== params.id)
              ) {
                return { redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT }
              }

              return <App />
            },
          },
        ],
      },
      {
        path: '/responders',
        async action({ next }) {
          const children = await next()

          return children
        },
        children: [
          {
            path: '/:id',
            async action(context) {
              const { state, params } = context

              storage.setItemType(ITEM_TYPE_RESPONDER)
              cancelStorage.setItemType(ITEM_TYPE_RESPONDER)

              state.savedItem = await loadItemByType(
                params.id,
                null,
                ITEM_TYPE_RESPONDER
              )
              state.savedItem.kind = ITEM_TYPE_RESPONDER

              state.editorModel.load(state.savedItem)
              state.fontloader.load(state.editorModel.getFonts())
              state.history.clear()

              if (
                !state.editorModel.id ||
                (state.editorModel.id && isFinite(Number(params.id))
                  ? parseInt(state.editorModel.id, 10) !==
                    parseInt(params.id, 10)
                  : state.editorModel.id !== params.id)
              ) {
                return { redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT }
              }

              return <App />
            },
          },
        ],
      },
      {
        path: '/forms',
        async action({ next }) {
          const children = await next()

          return children
        },
        children: [
          {
            path: '/:hash',
            async action({ next }) {
              const children = await next()

              return children
            },
            children: [
              {
                path: '/form',
                async action(context) {
                  const { state, params, next } = context

                  const wizard = await ganApi.getWizard(
                    params.hash,
                    WIZARD_TYPE_FORM
                  )
                  if (!wizard) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }
                  state.appModel.setWizard(wizard)
                  const item = (wizard.wizard || []).find(
                    (item) => item.kind === ITEM_TYPE_FORM
                  )
                  if (!item) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  state.appModel.setStepIndex(FORMS_STEP_INDEX_FORM)

                  captureReturnUrl(context, WIZARD_TYPE_FORM)

                  storage.setItemType(ITEM_TYPE_FORM)
                  cancelStorage.setItemType(ITEM_TYPE_FORM)

                  state.savedItem = await loadItemByType(
                    params.hash,
                    WIZARD_TYPE_FORM,
                    ITEM_TYPE_FORM
                  )
                  state.savedItem.kind = ITEM_TYPE_FORM
                  state.savedItem.wizardKind = WIZARD_TYPE_FORM

                  state.editorModel.load(state.savedItem)
                  state.fontloader.load(state.editorModel.getFonts())
                  state.history.clear()

                  if (
                    !state.editorModel.itemHash ||
                    (state.editorModel.itemHash && isFinite(Number(params.hash))
                      ? parseInt(state.editorModel.itemHash, 10) !==
                        parseInt(params.hash, 10)
                      : state.editorModel.itemHash !== params.hash)
                  ) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  return <App />
                },
              },
              {
                path: '/form-success',
                async action(context) {
                  const { state, params, next } = context

                  const wizard = await ganApi.getWizard(
                    params.hash,
                    WIZARD_TYPE_FORM
                  )
                  if (!wizard) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }
                  state.appModel.setWizard(wizard)
                  const item = (wizard.wizard || []).find(
                    (item) => item.kind === ITEM_TYPE_FORM
                  )
                  if (!item) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  state.appModel.setStepIndex(FORMS_STEP_INDEX_FORM_SUCCESS)

                  // Not currently hit with a real `next`: frontend-v3's publish
                  // screen edits the success message via the `/form` section
                  // (see FormEditorSection), not this route. Wired defensively
                  // for symmetry with the other steps and in case a future/older
                  // caller ever does link here directly. See FE-1218.
                  captureReturnUrl(context, WIZARD_TYPE_FORM)

                  storage.setItemType(ITEM_TYPE_FORM)
                  cancelStorage.setItemType(ITEM_TYPE_FORM)

                  state.savedItem = await loadItemByType(
                    params.hash,
                    WIZARD_TYPE_FORM,
                    ITEM_TYPE_FORM
                  )
                  state.savedItem.kind = ITEM_TYPE_FORM
                  state.savedItem.wizardKind = WIZARD_TYPE_FORM

                  state.editorModel.load(
                    state.savedItem,
                    ITEM_TYPE_FORM_SUBTYPE_SUCCESS
                  )
                  state.fontloader.load(state.editorModel.getFonts())
                  state.history.clear()

                  if (
                    !state.editorModel.itemHash ||
                    (state.editorModel.itemHash && isFinite(Number(params.hash))
                      ? parseInt(state.editorModel.itemHash, 10) !==
                        parseInt(params.hash, 10)
                      : state.editorModel.itemHash !== params.hash)
                  ) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  return <App />
                },
              },
              {
                path: '/mail',
                async action(context) {
                  const { state, params, next } = context

                  const wizard = await ganApi.getWizard(
                    params.hash,
                    WIZARD_TYPE_FORM
                  )
                  if (!wizard) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }
                  state.appModel.setWizard(wizard)
                  const item = (wizard.wizard || []).find(
                    (item) => item.kind === ITEM_TYPE_CONFIRMATION_MAIL
                  )
                  if (!item) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  state.appModel.setStepIndex(FORMS_STEP_INDEX_MAIL)

                  // frontend-v3's publish screen hands off directly into this
                  // step (the "edit mail" link), each with its own `next`. FE-1218.
                  captureReturnUrl(context, WIZARD_TYPE_FORM)

                  storage.setItemType(ITEM_TYPE_CONFIRMATION_MAIL)
                  cancelStorage.setItemType(ITEM_TYPE_CONFIRMATION_MAIL)

                  state.savedItem = await loadItemByType(
                    params.hash,
                    WIZARD_TYPE_FORM,
                    ITEM_TYPE_CONFIRMATION_MAIL
                  )
                  state.savedItem.kind = ITEM_TYPE_CONFIRMATION_MAIL
                  state.savedItem.wizardKind = WIZARD_TYPE_FORM

                  state.editorModel.load(state.savedItem)
                  state.fontloader.load(state.editorModel.getFonts())
                  state.history.clear()

                  if (
                    !state.editorModel.itemHash ||
                    (state.editorModel.itemHash && isFinite(Number(params.hash))
                      ? parseInt(state.editorModel.itemHash, 10) !==
                        parseInt(params.hash, 10)
                      : state.editorModel.itemHash !== params.hash)
                  ) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  return <App />
                },
              },
              {
                path: '/page',
                async action(context) {
                  const { state, params, next } = context

                  const wizard = await ganApi.getWizard(
                    params.hash,
                    WIZARD_TYPE_FORM
                  )
                  if (!wizard) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }
                  state.appModel.setWizard(wizard)
                  const item = (wizard.wizard || []).find(
                    (item) => item.kind === ITEM_TYPE_PAGE
                  )
                  if (!item) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  state.appModel.setStepIndex(FORMS_STEP_INDEX_PAGE)

                  // frontend-v3's publish screen hands off directly into this
                  // step (the "edit page" link), each with its own `next`. FE-1218.
                  captureReturnUrl(context, WIZARD_TYPE_FORM)

                  storage.setItemType(ITEM_TYPE_PAGE)
                  cancelStorage.setItemType(ITEM_TYPE_PAGE)

                  state.savedItem = await loadItemByType(
                    params.hash,
                    WIZARD_TYPE_FORM,
                    ITEM_TYPE_PAGE
                  )
                  state.savedItem.kind = ITEM_TYPE_PAGE
                  state.savedItem.wizardKind = WIZARD_TYPE_FORM

                  state.editorModel.load(state.savedItem)
                  state.fontloader.load(state.editorModel.getFonts())
                  state.history.clear()

                  if (
                    !state.editorModel.itemHash ||
                    (state.editorModel.itemHash && isFinite(Number(params.hash))
                      ? parseInt(state.editorModel.itemHash, 10) !==
                        parseInt(params.hash, 10)
                      : state.editorModel.itemHash !== params.hash)
                  ) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  return <App />
                },
              },
            ],
          },
        ],
      },
      {
        path: '/pages',
        async action({ next }) {
          const children = await next()

          return children
        },
        children: [
          {
            path: '/:hash',
            async action({ next }) {
              const children = await next()

              return children
            },
            children: [
              {
                path: '/landing-page',
                async action(context) {
                  const { state, params, next } = context

                  const wizard = await ganApi.getWizard(
                    params.hash,
                    WIZARD_TYPE_PAGE
                  )
                  if (!wizard) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }
                  state.appModel.setWizard(wizard)
                  const item = (wizard.wizard || []).find(
                    (item) => item.kind === ITEM_TYPE_LANDING_PAGE
                  )
                  if (!item) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  state.appModel.setStepIndex(PAGES_STEP_INDEX_PAGE)

                  captureReturnUrl(context, WIZARD_TYPE_PAGE)

                  storage.setItemType(ITEM_TYPE_LANDING_PAGE)
                  cancelStorage.setItemType(ITEM_TYPE_LANDING_PAGE)

                  state.savedItem = await loadItemByType(
                    params.hash,
                    WIZARD_TYPE_PAGE,
                    ITEM_TYPE_LANDING_PAGE
                  )
                  state.savedItem.kind = ITEM_TYPE_LANDING_PAGE
                  state.savedItem.wizardKind = WIZARD_TYPE_PAGE
                  state.savedItem.hash = params.hash

                  state.editorModel.load(state.savedItem)
                  state.fontloader.load(state.editorModel.getFonts())
                  state.history.clear()

                  if (
                    !state.editorModel.itemHash ||
                    (state.editorModel.itemHash && isFinite(Number(params.hash))
                      ? parseInt(state.editorModel.itemHash, 10) !==
                        parseInt(params.hash, 10)
                      : state.editorModel.itemHash !== params.hash)
                  ) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  return <App />
                },
              },
              {
                path: '/landing-page-success',
                async action(context) {
                  const { state, params, next } = context

                  const wizard = await ganApi.getWizard(
                    params.hash,
                    WIZARD_TYPE_PAGE
                  )
                  if (!wizard) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }
                  state.appModel.setWizard(wizard)
                  const item = (wizard.wizard || []).find(
                    (item) => item.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS
                  )
                  if (!item) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  state.appModel.setStepIndex(PAGES_STEP_INDEX_PAGE_SUCCESS)

                  // Not currently hit with a real `next` — landing pages have no
                  // distinct success-message content piece in frontend-v3 (see
                  // LandingPageEditorSection). Wired defensively for symmetry
                  // with the other steps. See FE-1216.
                  captureReturnUrl(context, WIZARD_TYPE_PAGE)

                  storage.setItemType(ITEM_TYPE_LANDING_PAGE_SUCCESS)
                  cancelStorage.setItemType(ITEM_TYPE_LANDING_PAGE_SUCCESS)

                  state.savedItem = await loadItemByType(
                    params.hash,
                    WIZARD_TYPE_PAGE,
                    ITEM_TYPE_LANDING_PAGE_SUCCESS
                  )
                  state.savedItem.kind = ITEM_TYPE_LANDING_PAGE_SUCCESS
                  state.savedItem.wizardKind = WIZARD_TYPE_PAGE
                  state.savedItem.hash = params.hash

                  state.editorModel.load(
                    state.savedItem,
                    ITEM_TYPE_LANDING_PAGE_SUCCESS
                  )
                  state.fontloader.load(state.editorModel.getFonts())
                  state.history.clear()

                  if (
                    !state.editorModel.itemHash ||
                    (state.editorModel.itemHash && isFinite(Number(params.hash))
                      ? parseInt(state.editorModel.itemHash, 10) !==
                        parseInt(params.hash, 10)
                      : state.editorModel.itemHash !== params.hash)
                  ) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  return <App />
                },
              },
              {
                path: '/mail',
                async action(context) {
                  const { state, params, next } = context

                  const wizard = await ganApi.getWizard(
                    params.hash,
                    WIZARD_TYPE_PAGE
                  )
                  if (!wizard) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }
                  state.appModel.setWizard(wizard)
                  const item = (wizard.wizard || []).find(
                    (item) => item.kind === ITEM_TYPE_CONFIRMATION_MAIL
                  )
                  if (!item) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  state.appModel.setStepIndex(PAGES_STEP_INDEX_MAIL)

                  // frontend-v3's publish screen hands off directly into this
                  // step (the "edit mail" link), each with its own `next`.
                  captureReturnUrl(context, WIZARD_TYPE_PAGE)

                  storage.setItemType(ITEM_TYPE_CONFIRMATION_MAIL)
                  cancelStorage.setItemType(ITEM_TYPE_CONFIRMATION_MAIL)

                  state.savedItem = await loadItemByType(
                    params.hash,
                    WIZARD_TYPE_PAGE,
                    ITEM_TYPE_CONFIRMATION_MAIL
                  )
                  state.savedItem.kind = ITEM_TYPE_CONFIRMATION_MAIL
                  state.savedItem.wizardKind = WIZARD_TYPE_PAGE
                  state.savedItem.hash = params.hash

                  state.editorModel.load(state.savedItem)
                  state.fontloader.load(state.editorModel.getFonts())
                  state.history.clear()

                  if (
                    !state.editorModel.itemHash ||
                    (state.editorModel.itemHash && isFinite(Number(params.hash))
                      ? parseInt(state.editorModel.itemHash, 10) !==
                        parseInt(params.hash, 10)
                      : state.editorModel.itemHash !== params.hash)
                  ) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  return <App />
                },
              },
              {
                path: '/page',
                async action(context) {
                  const { state, params, next } = context

                  const wizard = await ganApi.getWizard(
                    params.hash,
                    WIZARD_TYPE_PAGE
                  )
                  if (!wizard) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }
                  state.appModel.setWizard(wizard)
                  const item = (wizard.wizard || []).find(
                    (item) => item.kind === ITEM_TYPE_PAGE
                  )
                  if (!item) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  state.appModel.setStepIndex(
                    PAGES_STEP_INDEX_CONFIRMATION_PAGE
                  )

                  // frontend-v3's publish screen hands off directly into this
                  // step (the "edit page" link), each with its own `next`.
                  captureReturnUrl(context, WIZARD_TYPE_PAGE)

                  storage.setItemType(ITEM_TYPE_PAGE)
                  cancelStorage.setItemType(ITEM_TYPE_PAGE)

                  state.savedItem = await loadItemByType(
                    params.hash,
                    WIZARD_TYPE_PAGE,
                    ITEM_TYPE_PAGE
                  )
                  state.savedItem.kind = ITEM_TYPE_PAGE
                  state.savedItem.wizardKind = WIZARD_TYPE_PAGE
                  state.savedItem.hash = params.hash

                  state.editorModel.load(state.savedItem)
                  state.fontloader.load(state.editorModel.getFonts())
                  state.history.clear()

                  if (
                    !state.editorModel.itemHash ||
                    (state.editorModel.itemHash && isFinite(Number(params.hash))
                      ? parseInt(state.editorModel.itemHash, 10) !==
                        parseInt(params.hash, 10)
                      : state.editorModel.itemHash !== params.hash)
                  ) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  return <App />
                },
              },
            ],
          },
        ],
      },
      {
        path: '/surveys',
        async action({ next }) {
          const children = await next()

          return children
        },
        children: [
          {
            path: '/:hash',
            async action({ next }) {
              const children = await next()

              return children
            },
            children: [
              {
                path: '/survey',
                async action(context) {
                  const { state, params, next } = context

                  const wizard = await ganApi.getWizard(
                    params.hash,
                    WIZARD_TYPE_SURVEY
                  )
                  if (!wizard) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }
                  state.appModel.setWizard(wizard)
                  const item = (wizard.wizard || []).find(
                    (item) => item.kind === ITEM_TYPE_SURVEY
                  )
                  if (!item) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  state.appModel.setStepIndex(SURVEYS_STEP_INDEX_SURVEY)

                  storage.setItemType(ITEM_TYPE_SURVEY)
                  cancelStorage.setItemType(ITEM_TYPE_SURVEY)

                  state.savedItem = await loadItemByType(
                    params.hash,
                    WIZARD_TYPE_SURVEY,
                    ITEM_TYPE_SURVEY
                  )
                  state.savedItem.kind = ITEM_TYPE_SURVEY
                  state.savedItem.wizardKind = WIZARD_TYPE_SURVEY
                  state.savedItem.hash = params.hash

                  state.editorModel.load(state.savedItem)
                  state.fontloader.load(state.editorModel.getFonts())
                  state.history.clear()

                  if (
                    !state.editorModel.itemHash ||
                    (state.editorModel.itemHash && isFinite(Number(params.hash))
                      ? parseInt(state.editorModel.itemHash, 10) !==
                        parseInt(params.hash, 10)
                      : state.editorModel.itemHash !== params.hash)
                  ) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  return <App />
                },
              },
              {
                path: '/thank-you-page',
                async action(context) {
                  const { state, params, next } = context

                  const wizard = await ganApi.getWizard(
                    params.hash,
                    WIZARD_TYPE_SURVEY
                  )
                  if (!wizard) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }
                  state.appModel.setWizard(wizard)
                  const item = (wizard.wizard || []).find(
                    (item) => item.kind === ITEM_TYPE_SURVEY_THANK_YOU
                  )
                  if (!item) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  state.appModel.setStepIndex(SURVEYS_STEP_INDEX_THANK_YOU)

                  storage.setItemType(ITEM_TYPE_SURVEY_THANK_YOU)
                  cancelStorage.setItemType(ITEM_TYPE_SURVEY_THANK_YOU)

                  state.savedItem = await loadItemByType(
                    params.hash,
                    WIZARD_TYPE_SURVEY,
                    ITEM_TYPE_SURVEY_THANK_YOU
                  )
                  state.savedItem.kind = ITEM_TYPE_SURVEY_THANK_YOU
                  state.savedItem.wizardKind = WIZARD_TYPE_SURVEY
                  state.savedItem.hash = params.hash

                  state.editorModel.load(state.savedItem)
                  state.fontloader.load(state.editorModel.getFonts())
                  state.history.clear()

                  if (
                    !state.editorModel.itemHash ||
                    (state.editorModel.itemHash && isFinite(Number(params.hash))
                      ? parseInt(state.editorModel.itemHash, 10) !==
                        parseInt(params.hash, 10)
                      : state.editorModel.itemHash !== params.hash)
                  ) {
                    return {
                      redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
                    }
                  }

                  return <App />
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: /.*/,
    action: () => ({ redirect: APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT }),
  },
]

export default routes
